import F from '../../commons/HTTPFetcher';
import * as ContentTypes from '../../commons/constants/HTTPContentTypes';
import AlertAdapter from '../../alerts/models/Alert';
import {
  PENDING_UPDATE_STATUS_TRANSACTION_ALERT_ACTION,
  SUCCESS_UPDATE_STATUS_TRANSACTION_ALERT_ACTION,
  FAILURE_UPDATE_STATUS_TRANSACTION_ALERT_ACTION,
  FAILURE_CREATE_TRANSACTION_ALERT_ACTION,
  PENDING_CREATE_TRANSACTION_ALERT_ACTION,
  SUCCESS_CREATE_TRANSACTION_ALERT_ACTION,
  PENDING_GET_TRANSACTION_ALERT_ACTION,
  SUCCESS_GET_TRANSACTION_ALERT_ACTION,
  FAILURE_GET_TRANSACTION_ALERT_ACTION,
  FAILURE_UPDATE_TRANSACTION_ALERT_ACTION,
  PENDING_UPDATE_TRANSACTION_ALERT_ACTION,
  SUCCESS_UPDATE_TRANSACTION_ALERT_ACTION,
  RESET_TRANSACTION_ALERT_STATE_ACTION
} from '../actionsTypes';
import AlertConstants from '../../alerts/constants/AlertConstant';

export const updateTransactionAlertStatus = ({ id, status }) => async (dispatch) => {
  dispatch(updateTransactionAlertStatusStart());

  return F.put(`/alert/${id}/active/${status}`).then(
    () => dispatch(updateTransactionAlertStatusSucceeded({ id, status })),
    (errors) => {
      dispatch(updateTransactionAlertStatusFailure({ errors }));

      throw errors;
    }
  );
};

export const updateTransactionAlertStatusStart = () => ({
  type: PENDING_UPDATE_STATUS_TRANSACTION_ALERT_ACTION
});

export const updateTransactionAlertStatusSucceeded = ({ id, status }) => ({
  type: SUCCESS_UPDATE_STATUS_TRANSACTION_ALERT_ACTION,
  payload: {
    id,
    status
  }
});

export const updateTransactionAlertStatusFailure = ({ errors }) => ({
  type: FAILURE_UPDATE_STATUS_TRANSACTION_ALERT_ACTION,
  payload: {
    errors
  }
});

const addTriggerScope = ({ triggerScope, isCustomer }) => {
  if (triggerScope === 'onAnyOf') {
    if (isCustomer) {
      return { triggerScope: 'onAnyMerchant' };
    }
    return { triggerScope: 'onAnyStore' };
  }

  return { triggerScope: 'onAll' };
};

const mapCurrency = ({ criteria, currency: currencyCode }) => {
  return criteria === AlertConstants.SUM ? { currencyCode } : {};
};

const mapAlert = ({ alert, isCustomer }) => {
  const {
    id,
    name,
    criteria,
    active,
    filters,
    operator,
    triggerScope,
    sendWith,
    currency,
    timeWindow,
    value
  } = alert;

  return {
    id,
    name,
    active: !!active,
    threshold: {
      criteria: criteria?.value ?? criteria,
      operator: operator?.value ?? operator,
      value: parseFloat(value),
      timeWindow: timeWindow?.value ?? timeWindow,
      ...addTriggerScope({ triggerScope: triggerScope?.value || triggerScope, isCustomer }),
      ...mapCurrency({ criteria: criteria?.value ?? criteria, currency: currency?.value || currency })
    },
    filters,
    sendWith
  };
};

export const createTransactionAlert = ({ alert, isCustomer }) => (dispatch) => {
  dispatch(createTransactionAlertStart());

  const transactionAlert = mapAlert({ alert, isCustomer });
  return F.post('/alert', {
    body: transactionAlert,
    contentType: ContentTypes.JSON
  }).then(
    () => dispatch(createTransactionAlertSucceeded()),
    (errors) => {
      dispatch(createTransactionAlertFailure({ errors }));

      throw errors;
    }
  );
};

const createTransactionAlertStart = () => ({
  type: PENDING_CREATE_TRANSACTION_ALERT_ACTION
});

const createTransactionAlertSucceeded = () => ({
  type: SUCCESS_CREATE_TRANSACTION_ALERT_ACTION
});

const createTransactionAlertFailure = (errors: any) => ({
  type: FAILURE_CREATE_TRANSACTION_ALERT_ACTION,
  payload: {
    errors
  }
});

export const editTransactionAlert = ({ id, alert, isCustomer }) => (dispatch) => {
  dispatch(editTransactionAlertStart());

  const transactionAlert = mapAlert({ alert, isCustomer });
  return F.put(`/alert/${id}`, {
    body: transactionAlert,
    contentType: ContentTypes.JSON
  }).then(
    () => dispatch(editTransactionAlertSucceeded()),
    (errors) => {
      dispatch(editTransactionAlertFailure({ errors }));

      throw errors;
    }
  );
};

const editTransactionAlertStart = () => ({
  type: PENDING_UPDATE_TRANSACTION_ALERT_ACTION
});

const editTransactionAlertSucceeded = () => ({
  type: SUCCESS_UPDATE_TRANSACTION_ALERT_ACTION
});

const editTransactionAlertFailure = (errors) => ({
  type: FAILURE_UPDATE_TRANSACTION_ALERT_ACTION,
  payload: {
    errors
  }
});

const wrapFromApi = ({ alert }) => {
  const {
    id,
    name,
    active,
    sendWith,
    filters,
    threshold: { criteria, value, timeWindow, operator, triggerScope, currencyCode: currency }
  } = alert;

  const convertTriggerScope = ({ triggerScope }) => {
    switch (triggerScope) {
      case 'onAnyMerchant':
        return { triggerScope: 'onAnyOf' };
      case 'onAnyStore':
        return { triggerScope: 'onAnyOf' };
      default:
        return { triggerScope: 'onAll' };
    }
  };

  return {
    id,
    name,
    active: !!active,
    threshold: {
      criteria,
      operator: operator ? operator : '=',
      value: value.toString(),
      timeWindow,
      triggerScope,
      ...(currency ? { currency } : {}),
      ...convertTriggerScope({ triggerScope })
    },
    filters,
    sendWith
  };
};

export const getTransactionAlert = ({ id }) => (dispatch) => {
  dispatch(getTransactionAlertStart());

  return F.get(`/alert/${id}`).then(
    (alert) => {
      const transactionAlert = wrapFromApi({ alert });

      dispatch(getTransactionAlertSucceeded(transactionAlert));

      return transactionAlert;
    },
    (errors) => {
      dispatch(getTransactionAlertFailure(errors));

      throw errors;
    }
  );
};

const getTransactionAlertStart = () => ({
  type: PENDING_GET_TRANSACTION_ALERT_ACTION
});

const getTransactionAlertSucceeded = (transactionAlert) => ({
  type: SUCCESS_GET_TRANSACTION_ALERT_ACTION,
  payload: {
    transactionAlert
  }
});

const getTransactionAlertFailure = (errors) => ({
  type: FAILURE_GET_TRANSACTION_ALERT_ACTION,
  payload: {
    errors
  }
});

export const resetTransactionAlertState = () => ({
  type: RESET_TRANSACTION_ALERT_STATE_ACTION
});
